import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { AsyncPipe, NgClass, NgStyle } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	AlertComponent,
	ButtonComponent,
	ButtonIconComponent,
	INavHelp,
	LinkComponent,
	NavHelpComponent,
	PwcsLayoutComponent,
	QrComponent,
	SpinnerComponent,
	TileComponent,
	WatermarkComponent,
} from '@tecalisdev/infinity';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { ProgressService } from 'src/app/core/helpers/progress.service';
import { ILoading } from 'src/app/core/models/loading.models';
import { NavHelpAction } from 'src/app/core/models/navHelp.models';
import { AuthService } from 'src/app/core/services/auth.service';
import { LoadingService } from 'src/app/core/services/loading.service';
import { ScreenshotService } from 'src/app/core/services/screenshot.service';
import { PwcsInlineSvgComponent } from 'src/app/shared/components/pwcs-inline-svg/pwcs-inline-svg.component';
import { ModalScanQrComponent } from 'src/app/shared/components/modal-scan-qr/modal-scan-qr.component';
import { VideoComponent } from 'src/app/shared/components/video/video.component';
import { AudioService } from 'src/app/shared/services/audio.service';
import { CameraService } from 'src/app/shared/services/camera.service';
import { CameraPermissionsTimerService } from 'src/app/shared/services/camera-permissions-timer.service';
import { waitForVideoPreviewReady } from 'src/app/shared/utils/wait-for-video-preview-ready.util';
import {
	onCameraPreviewSwitchCompleted,
	onCameraPreviewSwitchStarted,
} from 'src/app/shared/utils/camera-preview-switch.util';
import { CloudLogsService, LogType } from 'src/app/shared/services/cloud-logs.service';
import { DesignService } from 'src/app/shared/services/config/design.service';
import { LanguagePickerUiService } from 'src/app/shared/services/language-picker-ui.service';
import { NavHelpService } from 'src/app/shared/services/nav-help.service';
import { DocsTimerManagerService } from 'src/app/user-identification/services/docs/timer-manager.service';
import { OcrService } from 'src/app/user-identification/services/ocr/ocr.service';
import { environment } from 'src/environments/environment';
import { DOCS_WARNING_TIME, DOCS_MAX_TAKE_PHOTO_TIME } from '../../../models/docs-check';
import { IDocsPhotoTakenEvent } from '../../../models/documents-check';
import {
	DOCS_TYPE,
	DocsLog,
	IDocumentsDocs,
	IDocumentsOperation,
	IPageWarning,
	PAGE_WARNING,
	quality,
} from '../../../models/user-identification';
import { HandsetLandscapeTileComponent } from 'src/app/shared/components/handset-landscape-tile/handset-landscape-tile.component';

@Component({
	selector: 'app-docs-take-photo',
	imports: [
		TranslatePipe,
		AsyncPipe,
		NgClass,
		NgStyle,
		AlertComponent,
		ButtonComponent,
		ButtonIconComponent,
		LinkComponent,
		NavHelpComponent,
		PwcsLayoutComponent,
		QrComponent,
		SpinnerComponent,
		TileComponent,
		WatermarkComponent,
		VideoComponent,
		HandsetLandscapeTileComponent,
		ModalScanQrComponent,
		PwcsInlineSvgComponent,
	],
	templateUrl: './docs-take-photo.component.html',
	styleUrls: ['./docs-take-photo.component.scss'],
})
export class DocsTakePhotoComponent implements OnInit, AfterViewInit, OnDestroy {
	@Input() doc: IDocumentsDocs;
	/** 0 = delantera, 1 = trasera (carnet con dos caras). */
	@Input() faceSlot?: 0 | 1;
	@Input() fileExtension: string = 'jpg';
	@Input()
	get operation(): IDocumentsOperation {
		return this._operation;
	}
	set operation(value: IDocumentsOperation) {
		this._operation = value;
		this.showLoading(this.trans.instant('GENERAL.LOADING.CAMERA_INIT'));
		this.activatePreviewLoading('GENERAL.LOADING.CAMERA_INIT');

		// Actualizamos el subtitle
		this.updateSubtitle();
	}
	private _operation: IDocumentsOperation;

	@Output() photoTaken = new EventEmitter<IDocsPhotoTakenEvent>();

	// Textos y alertas
	public subtitle: string = '';
	public helpTitle: string = '';

	// Camara
	public video!: HTMLVideoElement;
	public canvas!: HTMLCanvasElement;
	public ctx!: CanvasRenderingContext2D;
	public stream: MediaStream;
	public allCameras: Array<MediaDeviceInfo> = [];

	// Configuración de la prueba
	private subscriptionTimers: Subscription = new Subscription();

	// General
	public env = environment;
	public loading: ILoading = { show: false, text: '' };
	public isHelpByNavHelp: boolean = false;
	public isVisibleHelpNoPermissionsCamera: boolean = false;
	public isVisibleChangeDevice: boolean = false;
	public permissionsCamera: boolean = true;
	private cameraPermissionsReady = false;
	private cameraPermissionsTimerService = inject(CameraPermissionsTimerService);
	public navsHelp: Array<INavHelp> = this.navHelpService.getNavsHelp([NavHelpAction.QR]);
	public navsHelpTutorial: Array<INavHelp> = this.navHelpService.getTutorialNavsHelp();
	private destroy$: Subject<void> = new Subject<void>();
	private languagePickerUi = inject(LanguagePickerUiService);
	public pageWarning: IPageWarning = { show: false, type: null };
	public PAGE_WARNING = PAGE_WARNING;
	public readonly DOCS_TYPE = DOCS_TYPE;
	public handsetLandscape: boolean = false;
	public DocsLog: typeof DocsLog = DocsLog;
	public LogType: typeof LogType = LogType;
	public openQR: boolean = false;
	public isVisibleScan: boolean = false;
	public forceFlipHorizontal: boolean = true; // Forzar giro para poder leer bien los textos

	// Imagen capturada
	public capturedImage: string | null = null;

	/** Evita repetir el mismo audio de instrucción si el estado no cambia de clave. */
	private lastPlayedInstructionAudioKey: string | null = null;

	constructor(
		public progressService: ProgressService,
		public audioService: AudioService,
		public cloudLogsService: CloudLogsService,
		public ocrService: OcrService,
		public designService: DesignService,
		public auth: AuthService,
		private loadingService: LoadingService,
		private trans: TranslateService,
		private cameraService: CameraService,
		private screenShotService: ScreenshotService,
		private navHelpService: NavHelpService,
		private breakpointObserver: BreakpointObserver,
		private timerManagerService: DocsTimerManagerService,
	) {
		this.breakpointObserver
			.observe([Breakpoints.HandsetLandscape])
			.pipe(takeUntil(this.destroy$))
			.subscribe((result) => {
				this.handsetLandscape = result.matches;
				if (this.handsetLandscape) {
					this.stopTotalTime();
					if (!this.pageWarning.show) {
						this.startWarningTime();
					}
				} else {
					if (this.permissionsCamera) {
						this.startTotalTime();
					}
					if (!this.pageWarning.show) {
						this.stopWarningTime();
					}
				}
			});
	}

	ngOnInit(): void {
		this.trans.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
			void this.preloadAudiosForCurrentDoc();
			this.lastPlayedInstructionAudioKey = null;
			this.updateSubtitle();
		});

		this.cameraService.allCameras$.pipe(takeUntil(this.destroy$)).subscribe((cameras: MediaDeviceInfo[]) => {
			this.allCameras = cameras;

			const foundIndex = this.navsHelp.findIndex((nav: INavHelp) => nav.id === NavHelpAction.ROTATE);
			if (this.allCameras && this.allCameras.length > 1) {
				if (foundIndex === -1) {
					this.navsHelp = this.navHelpService.getNavsHelpWithCamera([NavHelpAction.QR]);
				}
			} else {
				if (foundIndex > -1) {
					this.navsHelp.splice(foundIndex, 1);
				}
			}
		});
	}

	ngAfterViewInit(): void {
		void this.preloadAudiosForCurrentDoc();
		this.checkCameraPermissions();

		if (window.location.hostname !== 'localhost' && environment.saveScreenShot) {
			this.screenShotService.startSaveScreenshot(DocsLog.SCREENSHOT);
		}
	}

	// #region Main Process
	public sendPhoto(status: boolean = true, options?: { permissionKo?: boolean }): void {
		if (!this.capturedImage && status && !options?.permissionKo) return;

		this.audioService.stop();

		let file: File | null = null;

		// Si hay imagen, la convertimos a archivo
		if (this.capturedImage) {
			file = this.dataURLtoFile(this.capturedImage, this.generateFileName());
		}

		this.photoTaken.emit({
			file,
			docUuid: this.doc.uuid,
			permissionKo: options?.permissionKo,
			debugImage: this.resolveDebugImage(options),
		});
	}

	private resolveDebugImage(options?: { permissionKo?: boolean }): string | undefined {
		if (this.capturedImage) {
			return this.capturedImage;
		}

		if (!options?.permissionKo || !this.video || this.video.videoWidth <= 0 || this.video.videoHeight <= 0) {
			return undefined;
		}

		try {
			const tempCanvas = document.createElement('canvas');
			tempCanvas.width = this.video.videoWidth;
			tempCanvas.height = this.video.videoHeight;
			const tempCtx = tempCanvas.getContext('2d');
			if (!tempCtx) {
				return undefined;
			}
			tempCtx.drawImage(this.video, 0, 0, tempCanvas.width, tempCanvas.height);
			return tempCanvas.toDataURL('image/jpeg', quality);
		} catch {
			return undefined;
		}
	}

	private generateFileName(): string {
		const timestamp = new Date().toISOString().replace(/[:.-]/g, '');
		const docType = this.doc.type || 'document';
		return `${docType}_${timestamp}.${this.fileExtension}`;
	}

	private getMimeTypeFromExtension(extension: string): string {
		const mimeMap: { [key: string]: string } = {
			jpg: 'image/jpeg',
			jpeg: 'image/jpeg',
			png: 'image/png',
		};
		return mimeMap[extension.toLowerCase()] || 'image/jpeg';
	}

	private dataURLtoFile(dataurl: string, filename: string): File {
		const arr = dataurl.split(',');
		const mime = arr[0].match(/:(.*?);/)![1];
		const bstr = atob(arr[1]);
		let n = bstr.length;
		const u8arr = new Uint8Array(n);
		while (n--) {
			u8arr[n] = bstr.charCodeAt(n);
		}
		return new File([u8arr], filename, { type: mime });
	}
	// #endregion

	// #region Handle Document
	/**
	 * Recorte 4:3 máximo inscrito en el buffer del vídeo, centrado en X e Y.
	 * Debe coincidir con `object-fit: cover` + `object-position: center` del `<video>` en `VideoComponent`.
	 */
	private getVideoCropForDriveLicense(
		videoW: number,
		videoH: number,
	): {
		cropW: number;
		cropH: number;
		srcX: number;
		srcY: number;
	} {
		const targetAspect = 4 / 3;
		let cropW = videoW;
		let cropH = cropW / targetAspect;
		if (cropH > videoH) {
			cropH = videoH;
			cropW = cropH * targetAspect;
		}
		return {
			cropW: Math.round(cropW),
			cropH: Math.round(cropH),
			srcX: Math.round((videoW - cropW) / 2),
			srcY: Math.round((videoH - cropH) / 2),
		};
	}

	public takePhoto(): void {
		if (!this.video || !this.canvas || !this.ctx) return;

		const vw = this.video.videoWidth;
		const vh = this.video.videoHeight;
		const isDriveLicense = this.doc?.type === DOCS_TYPE.DRIVE_LICENSE;
		const aspect = isDriveLicense ? 4 / 3 : 16 / 9;

		let srcX: number;
		let srcY: number;
		let srcWidth: number;
		let srcHeight: number;

		if (isDriveLicense) {
			const crop = this.getVideoCropForDriveLicense(vw, vh);
			srcX = crop.srcX;
			srcY = crop.srcY;
			srcWidth = crop.cropW;
			srcHeight = crop.cropH;
		} else {
			const tempWidth = vw;
			const tempHeight = tempWidth / aspect;
			srcX = Math.round((vw - tempWidth) / 2);
			srcY = Math.round(Math.max(0, (vh - tempHeight) / 2));
			srcWidth = Math.round(tempWidth);
			srcHeight = Math.round(tempHeight);
		}

		const tempCanvas = document.createElement('canvas');
		tempCanvas.width = srcWidth;
		tempCanvas.height = srcHeight;
		const tempCtx = tempCanvas.getContext('2d');

		if (!tempCtx) return;

		tempCtx.drawImage(this.video, srcX, srcY, srcWidth, srcHeight, 0, 0, tempCanvas.width, tempCanvas.height);

		// Almacenamos imagen
		const mimeType = this.getMimeTypeFromExtension(this.fileExtension);
		const img = tempCanvas.toDataURL(mimeType, quality);

		this.capturedImage = img;
		this.updateSubtitle();
	}

	public repeatPhoto(): void {
		this.capturedImage = null;
		this.updateSubtitle();
	}

	/** Limpia la foto tomada y actualiza textos para la siguiente cara (misma sesión de cámara). */
	public prepareNextFace(): void {
		this.repeatPhoto();
	}
	// #endregion

	// #region Nav Help
	public navHelpChange(action: INavHelp): void {
		const actions = {
			[NavHelpAction.ROTATE]: () => this.changeCamera(),
			[NavHelpAction.QR]: () => {
				this.isVisibleScan = true;
			},
			[NavHelpAction.HELP]: () => this.displayHelpWarning(true),
			[NavHelpAction.LANGUAGE]: () => this.languagePickerUi.open(),
			[NavHelpAction.VOLUMEN]: () => this.navHelpService.toggleAudioGuide(action),
		};
		return actions[action.id]();
	}
	// #endregion

	// #region Cámara
	private startWebcam(): void {
		if (this.permissionsCamera && !this.cameraService.hasGetUserMedia()) {
			this.setPageWarning(PAGE_WARNING.CAMERA_ERROR);
			this.cloudLogsService.sendCloudLogs(
				{ getUserMedia: 'getUserMedia is not supported by your browser' },
				DocsLog.PRELOAD,
				LogType.Error,
			);
			this.hideLoading();
			this.hidePreviewLoading();
		}
	}

	public onCameraError(err: any): void {
		this.hideLoading();
		this.hidePreviewLoading();
		if (err?.name === 'NotAllowedError') {
			this.permissionsCamera = false;
			this.updateSubtitle();
			this.onPermissionsDenied();
		} else {
			this.setPageWarning(PAGE_WARNING.CAMERA_ERROR);
			this.stopTotalTime();
			this.startWarningTime();
		}
	}

	public onCameraPermissionRevoked(): void {
		this.hideLoading();
		this.hidePreviewLoading();
		this.permissionsCamera = false;
		this.updateSubtitle();
		this.onPermissionsDenied();
	}

	public onCameraStarted(camera: {
		stream: MediaStream;
		video: HTMLVideoElement;
		canvas: HTMLCanvasElement;
		ctx: CanvasRenderingContext2D;
	}): void {
		this.video = camera.video;
		this.canvas = camera.canvas;
		this.ctx = camera.ctx;
		this.stream = camera.stream;

		if (!this.cameraPermissionsReady) {
			this.permissionsCamera = true;
			this.onPermissionsGranted();
		} else {
			this.stopTakePhotoTime();
			this.startTakePhotoTime();
		}

		void this.finishCameraPreviewStartup();
	}

	private async finishCameraPreviewStartup(): Promise<void> {
		try {
			await waitForVideoPreviewReady(this.video);
			this.hideLoading();
			this.getAllCameras();
			this.hidePreviewLoading();
			this.updateSubtitle();
		} catch (error: unknown) {
			this.cloudLogsService.sendCloudLogs(
				{
					docsCameraPreviewStartup: error instanceof Error ? error.message : String(error),
				},
				DocsLog.PRELOAD,
				LogType.Error,
			);
			this.hideLoading();
			this.hidePreviewLoading();
		}
	}

	private getAllCameras(): void {
		this.cameraService.getAllCameras();
	}

	private checkCameraPermissions(): void {
		const seq = this.cameraService.beginPermissionCheck();
		this.showLoading(this.trans.instant('GENERAL.LOADING.PERMISSIONS'));
		// Arrancamos el reloj justo al iniciar la petición de permisos: así el KO por timeout
		// también cubre el caso en que el usuario no responde al prompt del navegador.
		this.cameraPermissionsTimerService.startWait(() => this.onCameraPermissionsTimeout());
		this.cameraService
			.resolveHasCameraPermission(this.ocrService.constraints)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (hasPermission: boolean) => this.finishPermissionCheck(seq, hasPermission),
				error: () => this.finishPermissionCheck(seq, false),
			});
	}

	private finishPermissionCheck(seq: number, hasPermission: boolean): void {
		if (!this.cameraService.isPermissionCheckCurrent(seq)) {
			return;
		}
		if (hasPermission) {
			this.permissionsCamera = true;
			this.onPermissionsGranted();
			this.activatePreviewLoading('GENERAL.LOADING.CAMERA_STARTING');
			setTimeout(() => {
				this.startWebcam();
			}, 500);
		} else {
			this.permissionsCamera = false;
			this.updateSubtitle();
			this.onPermissionsDenied();
			this.hidePreviewLoading();
		}
		this.hideLoading();
	}

	private canRunTestTimers(): boolean {
		return this.permissionsCamera && this.cameraPermissionsReady;
	}

	private onPermissionsDenied(): void {
		this.stopActiveGuidanceAudio();
		this.cameraPermissionsReady = false;
		this.cameraService.releaseActiveStream();
		this.stopTakePhotoTime();
		this.stopTotalTime();
		this.stopWarningTime();
		this.cameraPermissionsTimerService.startWait(() => this.onCameraPermissionsTimeout());
	}

	private stopActiveGuidanceAudio(): void {
		this.audioService.stop();
		this.lastPlayedInstructionAudioKey = null;
	}

	private onPermissionsGranted(): void {
		this.cameraPermissionsTimerService.stopWait();
		this.cameraPermissionsReady = true;
		this.stopTakePhotoTime();
		this.stopTotalTime();
		this.timerManagerService.resetTotalTime();
		if (!this.handsetLandscape && !this.pageWarning.show) {
			this.startTotalTime();
			this.startTakePhotoTime();
		}
	}

	private onCameraPermissionsTimeout(): void {
		// `cameraPermissionsReady` solo pasa a true en onPermissionsGranted(): si seguimos aquí,
		// el usuario no ha concedido permisos (haya respondido NO o no haya respondido al prompt).
		if (this.cameraPermissionsReady) {
			return;
		}
		this.sendPhoto(false, { permissionKo: true });
	}

	public changeCamera(): void {
		this.cameraService.changeCamera();
	}

	public onCameraSwitchStarted(): void {
		onCameraPreviewSwitchStarted({
			activatePreviewLoading: (key) => this.activatePreviewLoading(key),
			hidePreviewLoading: () => this.hidePreviewLoading(),
		});
	}

	public onCameraSwitchCompleted(): void {
		onCameraPreviewSwitchCompleted({
			activatePreviewLoading: (key) => this.activatePreviewLoading(key),
			hidePreviewLoading: () => this.hidePreviewLoading(),
		});
	}
	// #endregion

	public updateSubtitle(): void {
		this.subtitle = this.generateSubtitle();
		this.maybePlayInstructionAudio();
	}

	private isDriveLicenseTwoFaceDoc(): boolean {
		return this.doc?.type === DOCS_TYPE.DRIVE_LICENSE && (this.doc.max ?? 1) > 1;
	}

	private async preloadAudiosForCurrentDoc(): Promise<void> {
		if (this.isDriveLicenseTwoFaceDoc()) {
			await this.audioService.preloadDocsDriveLicenseTakePhotoAudios();
		} else {
			await this.audioService.preloadDocsTakePhotoAudios();
		}
	}

	/**
	 * Reproduce el audio asociado al texto de instrucción actual (mismas claves que i18n / archivos en assets/audios).
	 */
	private maybePlayInstructionAudio(): void {
		const key = this.getCurrentInstructionAudioKey();
		if (!key || !this.audioService.audioGuideIsEnabled) {
			return;
		}
		if (key === this.lastPlayedInstructionAudioKey) {
			return;
		}
		this.lastPlayedInstructionAudioKey = key;
		this.audioService.stop();
		this.audioService.playAudio(key);
	}

	private getCurrentInstructionAudioKey(): string | null {
		if (!this.permissionsCamera || this.handsetLandscape) {
			return null;
		}
		if (this.loading.show) {
			return null;
		}
		if (this.isBlockingWarning()) {
			return null;
		}
		if (this.pageWarning.show && this.pageWarning.type !== PAGE_WARNING.HELP) {
			return null;
		}
		if (this.isHelpMode()) {
			return null;
		}

		if (this.capturedImage) {
			return 'OCR.MANUAL.CHECK_INFO';
		}

		if (
			this.doc?.type === DOCS_TYPE.DRIVE_LICENSE &&
			(this.faceSlot === 0 || this.faceSlot === 1) &&
			(this.doc.max ?? 1) > 1
		) {
			return this.faceSlot === 0 ? 'OCR.MANUAL.TAKE_PHOTO_FRONT_FIRST' : 'OCR.MANUAL.TAKE_PHOTO_BACK_SECOND';
		}

		return 'OCR.MANUAL.TAKE_PHOTO_DOC';
	}

	public checkPermissions(): void {
		this.checkCameraPermissions();
	}

	// #region Timer
	public startTest(): void {
		this.startTotalTime();
	}

	private startTakePhotoTime(): void {
		if (!this.canRunTestTimers()) return;
		this.timerManagerService.startTakePhotoTime(() => {
			if (this.timerManagerService.takePhotoTime >= DOCS_MAX_TAKE_PHOTO_TIME) {
				this.stopTakePhotoTime();
				this.sendPhoto(false);
			}
		});
	}

	public stopTakePhotoTime(): void {
		this.timerManagerService.stopTakePhotoTime();
	}

	// Total time
	private startTotalTime(): void {
		if (!this.canRunTestTimers()) return;
		this.timerManagerService.startTotalTime();
	}

	private stopTotalTime(): void {
		this.timerManagerService.stopTotalTime();
	}

	// Warning time
	private startWarningTime(): void {
		if (!this.canRunTestTimers()) return;
		this.timerManagerService.startWarningTime(() => {
			if (this.timerManagerService.warningTime >= DOCS_WARNING_TIME) {
				this.stopWarningTime();
				this.sendPhoto(false);
			}
		});
	}
	// #endregion

	public stopWarningTime(): void {
		this.timerManagerService.stopWarningTime();
	}

	private displayHelpWarning(isHelpByNavHelp: boolean): void {
		this.isHelpByNavHelp = isHelpByNavHelp;
		this.setPageHelp();
		this.stopTotalTime();
		this.startWarningTime();
	}

	/**
	 * Establece la ayuda de la página según el tipo especificado.
	 *
	 * @param type Tipo de ayuda a mostrar: 'anv_rev' para revisión de anverso, 'valid_docs' para documentos válidos. Por defecto es 'anv_rev'.
	 *
	 * Actualiza el título de la ayuda, la visibilidad de la sección de documentos válidos
	 * y muestra la advertencia de ayuda correspondiente.
	 */
	private setPageHelp(type: 'anv_rev' | 'valid_docs' | 'invalid_doc' | 'change_device' = 'anv_rev'): void {
		const isHelpDocs = type !== 'anv_rev' && type !== 'change_device';

		let helpDocTitle = 'OCR.HELP.TITLE_VALID_DOCUMENTS';
		if (type === 'invalid_doc') helpDocTitle = 'OCR.HELP.TITLE_INVALID_DOCUMENT';

		this.helpTitle = isHelpDocs ? helpDocTitle : 'LIFE_CHECK.HELP.TITLE';
		this.setPageWarning(PAGE_WARNING.HELP);
	}

	/**
	 * Establece el tipo y la visibilidad de la advertencia en la página.
	 *
	 * Esta función actualiza el objeto `pageWarning` para mostrar u ocultar una advertencia en la interfaz,
	 * y define el tipo de advertencia que se debe mostrar.
	 *
	 * @param type El tipo de advertencia de la página, puede ser `PAGE_WARNING` o `null`.
	 * @param show Indica si se debe mostrar la advertencia en la página. Por defecto es `true`.
	 */
	public setPageWarning(type: PAGE_WARNING | null, show: boolean = true): void {
		this.pageWarning.show = show;
		this.pageWarning.type = type;

		// Actualizamos el subtitle
		this.updateSubtitle();
	}

	// #region Gestión de los textos
	/**
	 * Genera el subtítulo adecuado para la interfaz de usuario del componente OCR,
	 * basado en el estado actual de los permisos, advertencias, configuración de la operación,
	 * y el progreso del reconocimiento de documentos.
	 *
	 * @returns {string} El subtítulo traducido correspondiente al estado actual, o una cadena vacía si no aplica.
	 */
	private generateSubtitle(): string {
		if (this.shouldReturnEmptySubtitle()) return '';
		if (this.isHelpMode()) return this.getHelpSubtitle();
		if (this.isLoadingOrWarning()) return '';
		return this.getDocumentSelectionSubtitle();
	}

	/**
	 * Determina si el subtítulo debe estar vacío debido a condiciones bloqueantes.
	 *
	 * @returns {boolean} True si no hay permisos de cámara, está en landscape o hay advertencias bloqueantes.
	 */
	private shouldReturnEmptySubtitle(): boolean {
		return !this.permissionsCamera || this.handsetLandscape || this.isBlockingWarning();
	}

	/**
	 * Verifica si hay una advertencia que bloquea la visualización de subtítulos.
	 *
	 * @returns {boolean} True si el tipo de advertencia es bloqueante.
	 */
	private isBlockingWarning(): boolean {
		return [PAGE_WARNING.VERIFICATION_FAIL, PAGE_WARNING.SERVER_ERROR, PAGE_WARNING.CAMERA_ERROR].includes(
			this.pageWarning.type,
		);
	}

	/**
	 * Verifica si la interfaz está en modo ayuda.
	 *
	 * @returns {boolean} True si se está mostrando la ayuda.
	 */
	private isHelpMode(): boolean {
		return this.pageWarning.type === PAGE_WARNING.HELP;
	}

	/**
	 * Obtiene el subtítulo para el modo ayuda.
	 *
	 * @returns {string} El texto traducido de ayuda.
	 */
	private getHelpSubtitle(): string {
		return this.trans.instant('OCR.BACK.HELP.DESCRIPTION');
	}

	/**
	 * Verifica si se está mostrando el loading o hay advertencias activas.
	 *
	 * @returns {boolean} True si hay loading, advertencias o landscape activo.
	 */
	private isLoadingOrWarning(): boolean {
		return this.loading.show || this.pageWarning.show || this.handsetLandscape;
	}

	/**
	 * Obtiene el subtítulo para la selección y captura de documentos.
	 *
	 * @returns {string} El texto traducido según el estado de selección y captura del documento.
	 */
	private getDocumentSelectionSubtitle(): string {
		if (this.capturedImage) {
			const message = 'OCR.MANUAL.CHECK_INFO';
			const translatedMessage = this.trans.instant(message);
			return translatedMessage;
		}

		return this.getPhotoInstructionSubtitle();
	}

	/**
	 * Obtiene el subtítulo de instrucciones para tomar la foto del documento.
	 *
	 * @returns {string} El texto traducido con instrucciones específicas según el tipo y lado del documento.
	 */
	private getPhotoInstructionSubtitle(): string {
		let title = this.doc.title || this.trans.instant(`DOCUMENTS.TYPES.${this.doc.type}`);
		if (title && title !== title.toUpperCase()) title = title.toLowerCase();

		if (
			this.doc?.type === DOCS_TYPE.DRIVE_LICENSE &&
			(this.faceSlot === 0 || this.faceSlot === 1) &&
			(this.doc.max ?? 1) > 1
		) {
			const message =
				this.faceSlot === 0 ? 'OCR.MANUAL.TAKE_PHOTO_FRONT_FIRST' : 'OCR.MANUAL.TAKE_PHOTO_BACK_SECOND';
			return this.trans.instant(message, { title });
		}

		const message = 'OCR.MANUAL.TAKE_PHOTO_DOC';
		return this.trans.instant(message, { title });
	}
	// #endregion

	// #region Loading
	/**
	 * Muestra un indicador de carga personalizado según la configuración de la operación.
	 * Si la operación utiliza un iframe, muestra el indicador de carga en el iframe;
	 * de lo contrario, muestra el indicador de carga estándar.
	 *
	 * @param text Texto opcional que se mostrará junto al indicador de carga.
	 */
	private showLoading(text = ''): void {
		this.loadingService.show(text);
	}

	/**
	 * Oculta el indicador de carga personalizado.
	 * Si la configuración de la operación utiliza un iframe, oculta el indicador de carga específico para iframes;
	 * de lo contrario, oculta el indicador de carga estándar.
	 */
	private hideLoading(): void {
		this.loadingService.hide();
	}

	private activatePreviewLoading(textKey: string): void {
		this.loading = {
			show: true,
			text: this.trans.instant(textKey),
		};
	}

	private hidePreviewLoading(): void {
		this.loading = { show: false, text: '' };
	}
	// #endregion

	ngOnDestroy(): void {
		this.cameraPermissionsTimerService.stopWait();
		// Desuscribirse de todos los timers
		this.video = null;
		this.stopTotalTime();
		if (this.subscriptionTimers) {
			this.subscriptionTimers.unsubscribe();
		}
		if (this.stream) {
			this.stream.getTracks().forEach((track) => track.stop());
		}
		this.stream = null;
		this.screenShotService.stopSaveScreenshot();
		this.destroy$.next();
		this.destroy$.complete();
	}
}
